import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import './SubPlaceModal.css';

export interface SubPlace {
    subPlaceId: number;
    meetingId?: string;
    placeId?: string;          // 장소 ID (place 테이블 참조)
    placeName: string;
    placeAddress?: string;
    latitude?: number;
    longitude?: number;
    category: string;
    sortOrder: number;
    dayNumber?: number;
    visitTime?: string;
    description?: string;
    imageUrl?: string;
    rating?: number;
    priceRange?: string;
    tags?: string;
    createdAt?: string;
}

export interface SubPlaceForm {
    placeName: string;
    placeAddress: string;
    category: string;
    dayNumber: number;
    visitTime: string;
    description: string;
}

interface SubPlaceModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetingId: string | undefined;
    editingSubPlace: SubPlace | null;
    isTravelMeeting: boolean;
    travelPeriod: { days: number; nights: number; start: Date; end: Date } | null;
    meetingTime?: string;
    subPlaces: SubPlace[];
    onSaveSuccess: () => void;
    returnTo: string;
    initialForm?: SubPlaceForm;
    initialIsCustomVisitTime?: boolean;
}

// Default visit time options
const allVisitTimeOptions = [
    { value: '06:00', label: '오전 6시' },
    { value: '07:00', label: '오전 7시' },
    { value: '08:00', label: '오전 8시' },
    { value: '09:00', label: '오전 9시' },
    { value: '10:00', label: '오전 10시' },
    { value: '11:00', label: '오전 11시' },
    { value: '12:00', label: '낮 12시' },
    { value: '13:00', label: '오후 1시' },
    { value: '14:00', label: '오후 2시' },
    { value: '15:00', label: '오후 3시' },
    { value: '16:00', label: '오후 4시' },
    { value: '17:00', label: '오후 5시' },
    { value: '18:00', label: '오후 6시' },
    { value: '19:00', label: '오후 7시' },
    { value: '20:00', label: '오후 8시' },
    { value: '21:00', label: '오후 9시' },
    { value: '22:00', label: '오후 10시' },
];

// Parse time string to minutes
const parseTimeToMinutes = (timeStr: string): number | null => {
    if (!timeStr) return null;
    const hhmmMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (hhmmMatch) {
        return parseInt(hhmmMatch[1]) * 60 + parseInt(hhmmMatch[2]);
    }
    const koreanMatch = timeStr.match(/(오전|오후|낮)\s*(\d{1,2})(?:시|:)?\s*(\d{0,2})?/);
    if (koreanMatch) {
        let hour = parseInt(koreanMatch[2]);
        const minute = koreanMatch[3] ? parseInt(koreanMatch[3]) : 0;
        if (koreanMatch[1] === '오후' && hour !== 12) hour += 12;
        else if (koreanMatch[1] === '오전' && hour === 12) hour = 0;
        else if (koreanMatch[1] === '낮') hour = 12;
        return hour * 60 + minute;
    }
    return null;
};

// Category labels
const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
        'PRIMARY': '1차',
        'SECONDARY': '2차',
        'TERTIARY': '3차',
        'ATTRACTION': '관광지',
        'RESTAURANT': '맛집',
        'CAFE': '카페',
        'HOTEL': '숙소'
    };
    return labels[category] || category;
};

// Category icons
const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
        'PRIMARY': '1️⃣',
        'SECONDARY': '2️⃣',
        'TERTIARY': '3️⃣',
        'ATTRACTION': '🏛️',
        'RESTAURANT': '🍽️',
        'CAFE': '☕',
        'HOTEL': '🏨'
    };
    return icons[category] || '📍';
};

export const SubPlaceModal = ({
    isOpen,
    onClose,
    meetingId,
    editingSubPlace,
    isTravelMeeting,
    travelPeriod,
    meetingTime,
    subPlaces,
    onSaveSuccess,
    returnTo,
    initialForm,
    initialIsCustomVisitTime
}: SubPlaceModalProps) => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();

    const defaultForm: SubPlaceForm = {
        placeName: '',
        placeAddress: '',
        category: isTravelMeeting ? 'ATTRACTION' : 'PRIMARY',
        dayNumber: 1,
        visitTime: '',
        description: ''
    };

    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm>(initialForm || defaultForm);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(initialIsCustomVisitTime || false);

    // Reset form when modal opens or editing changes
    useEffect(() => {
        if (isOpen) {
            if (initialForm) {
                setSubPlaceForm(initialForm);
                setIsCustomVisitTime(initialIsCustomVisitTime || false);
            } else if (editingSubPlace) {
                const visitTime = editingSubPlace.visitTime || '';
                const isPreset = allVisitTimeOptions.some(opt => opt.value === visitTime);
                setSubPlaceForm({
                    placeName: editingSubPlace.placeName,
                    placeAddress: editingSubPlace.placeAddress || '',
                    category: editingSubPlace.category,
                    dayNumber: editingSubPlace.dayNumber || 1,
                    visitTime: visitTime,
                    description: editingSubPlace.description || ''
                });
                setIsCustomVisitTime(visitTime !== '' && !isPreset);
            } else {
                setSubPlaceForm(defaultForm);
                setIsCustomVisitTime(false);
            }
        }
    }, [isOpen, editingSubPlace, initialForm, initialIsCustomVisitTime, isTravelMeeting]);

    // Filter visit time options based on meeting time
    const visitTimeOptions = useMemo(() => {
        const meetingMinutes = parseTimeToMinutes(meetingTime || '');
        if (meetingMinutes === null) return allVisitTimeOptions;
        return allVisitTimeOptions.filter(option => {
            const optionMinutes = parseTimeToMinutes(option.value);
            return optionMinutes !== null && optionMinutes >= meetingMinutes;
        });
    }, [meetingTime]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!meetingId || !subPlaceForm.placeName.trim()) {
            await showAlert({ message: '장소명을 입력해주세요.' });
            return;
        }

        try {
            if (editingSubPlace) {
                // Update
                await MeetingRepository.onPutUpdateSubPlace(meetingId, editingSubPlace.subPlaceId, {
                    placeName: subPlaceForm.placeName,
                    placeAddress: subPlaceForm.placeAddress,
                    category: subPlaceForm.category,
                    dayNumber: subPlaceForm.dayNumber,
                    visitTime: subPlaceForm.visitTime,
                    description: subPlaceForm.description
                });
                onClose();
                await showAlert({ message: '장소가 수정되었습니다.' });
            } else {
                // Create
                const sortOrder = (subPlaces || []).filter(sp => sp.category === subPlaceForm.category).length + 1;
                await MeetingRepository.onPostCreateSubPlace(meetingId, {
                    placeName: subPlaceForm.placeName,
                    placeAddress: subPlaceForm.placeAddress,
                    category: subPlaceForm.category,
                    sortOrder,
                    dayNumber: isTravelMeeting ? subPlaceForm.dayNumber : undefined,
                    visitTime: subPlaceForm.visitTime,
                    description: subPlaceForm.description
                });
                onClose();
                await showAlert({ message: '장소가 추가되었습니다.' });
            }
            onSaveSuccess();
        } catch (error) {
            console.error('서브 장소 저장 실패:', error);
            await showAlert({ message: '장소 저장에 실패했습니다.' });
        }
    }, [meetingId, subPlaceForm, editingSubPlace, subPlaces, isTravelMeeting, onClose, onSaveSuccess, showAlert]);

    // Handle map search navigation
    const handleMapSearch = useCallback(() => {
        navigate('/meeting/place-search', {
            state: {
                returnTo,
                fromSubPlaceModal: true,
                subPlaceForm,
                editingSubPlaceId: editingSubPlace?.subPlaceId,
                isCustomVisitTime,
                meetingId // meetingId를 전달하여 돌아올 때도 유지
            }
        });
    }, [navigate, returnTo, subPlaceForm, editingSubPlace, isCustomVisitTime, meetingId]);

    // Handle time picker navigation
    const handleTimePicker = useCallback(() => {
        navigate('/meeting/time-picker', {
            state: {
                returnTo,
                fromSubPlaceModal: true,
                subPlaceForm,
                editingSubPlaceId: editingSubPlace?.subPlaceId,
                meetingId // meetingId를 전달하여 돌아올 때도 유지
            }
        });
    }, [navigate, returnTo, subPlaceForm, editingSubPlace, meetingId]);

    if (!isOpen) return null;

    return (
        <div className="sub-place-modal-overlay" onClick={onClose}>
            <div className="sub-place-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sub-place-modal-header">
                    <h2 className="sub-place-modal-title">
                        {editingSubPlace ? '장소 수정' : '장소 추가'}
                    </h2>
                    <button className="sub-place-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="sub-place-modal-content">
                    {/* Map search button */}
                    <div className="modal-form-group">
                        <button
                            type="button"
                            className="map-search-btn"
                            onClick={handleMapSearch}
                        >
                            <span className="map-search-icon">🗺️</span>
                            <span className="map-search-text">지도에서 장소 검색</span>
                            <span className="map-search-arrow">›</span>
                        </button>
                    </div>

                    {/* Place name */}
                    <div className="modal-form-group">
                        <label className="modal-form-label">장소명 *</label>
                        <input
                            type="text"
                            className="modal-form-input"
                            placeholder="예: 성산일출봉, 흑돼지 맛집"
                            value={subPlaceForm.placeName}
                            onChange={(e) => setSubPlaceForm({ ...subPlaceForm, placeName: e.target.value })}
                        />
                    </div>

                    {/* Address */}
                    <div className="modal-form-group">
                        <label className="modal-form-label">주소 (선택)</label>
                        <input
                            type="text"
                            className="modal-form-input"
                            placeholder="상세 주소를 입력하세요"
                            value={subPlaceForm.placeAddress}
                            onChange={(e) => setSubPlaceForm({ ...subPlaceForm, placeAddress: e.target.value })}
                        />
                    </div>

                    {/* Category - only for travel meetings */}
                    {isTravelMeeting && (
                        <div className="modal-form-group">
                            <label className="modal-form-label">카테고리</label>
                            <div className="category-chips">
                                {['ATTRACTION', 'RESTAURANT', 'CAFE', 'HOTEL'].map(cat => (
                                    <button
                                        key={cat}
                                        type="button"
                                        className={`category-chip ${subPlaceForm.category === cat ? 'selected' : ''}`}
                                        onClick={() => setSubPlaceForm({ ...subPlaceForm, category: cat })}
                                    >
                                        {getCategoryIcon(cat)} {getCategoryLabel(cat)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Day selection - only for travel meetings */}
                    {isTravelMeeting && travelPeriod && (
                        <div className="modal-form-group">
                            <label className="modal-form-label">방문 일차</label>
                            <div className="day-chips">
                                {Array.from({ length: travelPeriod.days }, (_, i) => i + 1).map(day => (
                                    <button
                                        key={day}
                                        type="button"
                                        className={`day-chip ${subPlaceForm.dayNumber === day ? 'selected' : ''}`}
                                        onClick={() => setSubPlaceForm({ ...subPlaceForm, dayNumber: day })}
                                    >
                                        DAY {day}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Visit time */}
                    <div className="modal-form-group">
                        <label className="modal-form-label">방문 시간 (선택)</label>
                        <div className="visit-time-chips">
                            {visitTimeOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`visit-time-chip ${!isCustomVisitTime && subPlaceForm.visitTime === option.value ? 'selected' : ''}`}
                                    onClick={() => {
                                        setIsCustomVisitTime(false);
                                        setSubPlaceForm({ ...subPlaceForm, visitTime: option.value });
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="visit-time-chip direct"
                                onClick={handleTimePicker}
                            >
                                직접 입력
                            </button>
                        </div>

                        {/* Show custom time if selected */}
                        {isCustomVisitTime && subPlaceForm.visitTime && (
                            <div className="selected-custom-visit-time">
                                <div className="custom-time-label">선택한 시간</div>
                                <div className="custom-time-value">{subPlaceForm.visitTime}</div>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div className="modal-form-group">
                        <label className="modal-form-label">메모 (선택)</label>
                        <textarea
                            className="modal-form-textarea"
                            placeholder="장소에 대한 메모를 남겨보세요"
                            value={subPlaceForm.description}
                            onChange={(e) => setSubPlaceForm({ ...subPlaceForm, description: e.target.value })}
                            rows={3}
                        />
                    </div>
                </div>

                <div className="sub-place-modal-footer">
                    <button className="modal-cancel-btn" onClick={onClose}>
                        취소
                    </button>
                    <button className="modal-save-btn" onClick={handleSave}>
                        {editingSubPlace ? '수정' : '추가'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export { getCategoryLabel, getCategoryIcon };
